/**
 * TicketModals Component
 *
 * Modal dialogs for ticket association functionality (Jira, Azure DevOps, etc.).
 * Extracted from VulnerabilityDrawer.tsx to avoid loading heavy dependencies
 * (like jszip from ImportVulnerabilitiesModal) when only ticket modals are needed.
 *
 * @module sections/vulnerabilities/components
 */

import { useEffect, useMemo, useState } from 'react';

import { useTheme } from '@/app/ThemeContext';
import { InputText } from '@/components/form/InputText';
import { Modal } from '@/components/Modal';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAssociateTicket } from '@/hooks/ticket';
import { AllIntegrations } from '@/hooks/useIntegration';
import { getQueryKey } from '@/hooks/useQueryKeys';
import {
  getTicketingIntegration,
  useTicketingAccounts,
} from '@/hooks/useTicketingIntegrations';
import { queryClient } from '@/queryclient';
import SetupModal from '@/sections/SetupModal';
import { useGlobalState } from '@/state/global.state';
import { Account } from '@/types';
import type { DropdownOption } from '@/types/dropdown.types';

export function TicketModals() {
  const { mutateAsync: associateTicket } = useAssociateTicket();
  const { currentTheme } = useTheme();
  const {
    modal: { ticket },
  } = useGlobalState();

  // Fetch accounts for all ticketing integrations (config-driven)
  const { accounts, isLoading } = useTicketingAccounts();

  const [associatedTicketId, setAssociatedTicketId] = useState<string | null>(
    null
  );
  const [selectedAccountOption, setSelectedAccountOption] = useState<
    DropdownOption | undefined
  >();

  const integrationIcon = (member: string) => {
    const config = getTicketingIntegration(member);
    return (
      <img
        src={`/icons/${currentTheme}/${config?.logo}`}
        alt={config?.name ?? 'Ticketing'}
        className="size-6"
      />
    );
  };

  const selectedAccount: Account | undefined = accounts?.find(
    account => account.key === selectedAccountOption?.value
  );

  const SelectedIcon = selectedAccount
    ? integrationIcon(selectedAccount.member)
    : integrationIcon('jira');

  const accountsString = useMemo(() => JSON.stringify(accounts), [accounts]);

  // Use setTimeout to defer setState, avoiding synchronous cascading renders
  useEffect(() => {
    if (accounts?.length) {
      const timer = setTimeout(() => {
        setSelectedAccountOption({
          label: accounts[0].settings?.displayName ?? '',
          value: accounts[0].key,
        });
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [accountsString]);

  useEffect(() => {
    if (ticket.associateTicket.open) {
      const timer = setTimeout(() => setAssociatedTicketId(null), 0);
      return () => {
        clearTimeout(timer);
        setAssociatedTicketId(null);
      };
    }
  }, [ticket.associateTicket.open]);

  return (
    <>
      <Modal
        baseClassName="overflow-visible"
        open={ticket.associateTicket.open}
        onClose={() => ticket.associateTicket.onOpenChange(false)}
        title={
          <div className="flex w-full flex-row items-center justify-between">
            <p>Associate Existing Ticket</p>
            <Select
              value={
                selectedAccountOption?.value !== undefined
                  ? String(selectedAccountOption.value)
                  : ''
              }
              onValueChange={value => {
                const account = accounts?.find(a => a.key === value);
                if (account) {
                  setSelectedAccountOption({
                    label: account.settings?.displayName ?? '',
                    value: account.key,
                  });
                }
              }}
            >
              <SelectTrigger className="max-w-[200px] gap-2 [&>span]:overflow-hidden [&>span]:text-ellipsis">
                <span className="flex items-center gap-2 overflow-hidden">
                  {SelectedIcon}
                  <SelectValue placeholder="Select ticketing account" />
                </span>
              </SelectTrigger>
              <SelectContent className="z-200">
                {(accounts ?? [])
                  .filter(account => account.key !== '')
                  .map(account => (
                    <SelectItem key={account.key} value={account.key}>
                      <span className="flex items-center gap-2">
                        {integrationIcon(account.member)}
                        {account.settings?.displayName ?? ''}
                      </span>
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
        }
        footer={{
          text: 'Save',
          form: 'associate-ticket-form',
          disabled: isLoading,
        }}
      >
        <form
          id="associate-ticket-form"
          onSubmit={async e => {
            e.preventDefault();
            const promises = ticket.associateTicket?.riskKeys?.map(
              async riskKey => {
                await associateTicket({
                  account: String(selectedAccountOption?.value || ''),
                  riskKey,
                  ticketId: associatedTicketId || '',
                });
              }
            );
            await Promise.all(promises);

            queryClient.invalidateQueries({
              queryKey: getQueryKey.genericSearch(''),
            });

            ticket.associateTicket.onOpenChange(false);
          }}
        >
          <label className="flex flex-col gap-1">
            Ticket ID
            <InputText
              name="associatedTicketId"
              value={associatedTicketId || ''}
              onChange={e => setAssociatedTicketId(e.target.value)}
              placeholder="Enter existing ticket ID"
              required
            />
          </label>
        </form>
      </Modal>
      {ticket.setup.open && (
        <SetupModal
          isOpen={ticket.setup.open}
          onClose={() => ticket.setup.onOpenChange(false)}
          onComplete={() => ticket.setup.onOpenChange(false)}
          selectedIntegration={undefined}
          formData={{}}
          integration={AllIntegrations.jira}
        />
      )}
    </>
  );
}
